import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { api } from "../../auth/api";
import { useAuth } from "../../auth/AuthContext";
import { parseMembersSpreadsheet } from "./parseMembersSpreadsheet";
import "../members/Members.css";

const TABS = [
  { id: "usage", label: "Email usage" },
  { id: "pending", label: "Pending access" },
  { id: "members", label: "Members" },
  { id: "bulk", label: "Bulk import" },
  { id: "audit", label: "Audit log" },
];

function formatLastLogin(value) {
  if (!value) return "Never";
  const s = String(value).trim();
  const iso = s.includes("T") ? s : `${s.replace(" ", "T")}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function usageStatusLabel(usage) {
  if (usage.active_provider === "log") return "Local log mode (no mail sent)";
  if (usage.active_provider === "resend") {
    return "Fallback: sending via Resend (Postmark monthly quota reached)";
  }
  if (usage.active_provider === "exhausted") {
    if (!usage.resend?.configured && usage.postmark?.at_or_over_limit) {
      return "Postmark quota reached and Resend is not configured — mail is paused";
    }
    return "Both providers are at quota — mail is paused";
  }
  return "Sending via Postmark";
}

function UsageBanner({ usage }) {
  if (usage.active_provider === "exhausted") {
    return (
      <div className="usage-over">
        {usage.resend?.configured
          ? "Postmark and Resend quotas are both exhausted. Magic links and contact emails are paused until a UTC day/month reset, or until limits are raised."
          : "Monthly Postmark limit reached and Resend is not configured. Magic links and contact emails are paused until next UTC month (or set RESEND_API_KEY / raise POSTMARK_MONTHLY_LIMIT)."}
      </div>
    );
  }
  if (usage.active_provider === "resend") {
    const dailyWarn =
      usage.resend.sent_today >= usage.resend.daily_warn_threshold;
    const monthWarn =
      usage.resend.sent_this_month >= usage.resend.monthly_warn_threshold;
    return (
      <div className={dailyWarn || monthWarn ? "usage-over" : "usage-warn"}>
        Postmark is at its monthly limit. New mail is going through Resend
        {dailyWarn
          ? ` — approaching the Resend daily cap (${usage.resend.daily_limit}/UTC day).`
          : monthWarn
            ? ` — approaching the Resend monthly cap (${usage.resend.monthly_limit}/month).`
            : "."}
      </div>
    );
  }
  if (usage.postmark?.sent_this_month >= usage.postmark?.warn_threshold) {
    return (
      <div className="usage-warn">
        Approaching the Postmark limit of {usage.postmark.monthly_limit}{" "}
        emails/month. Resend
        {usage.resend?.configured ? " will" : " is not configured to"} take over
        if this cap is hit.
      </div>
    );
  }
  return <p className="help-text">{usage.note}</p>;
}

function UsagePanel() {
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .adminEmailUsage()
      .then(setUsage)
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="message message-error">{error}</div>;
  if (!usage) return <p>Loading usage…</p>;

  const postmark = usage.postmark || {};
  const resend = usage.resend || {};
  const byKind = usage.by_kind_month || {};

  return (
    <div>
      <p className="usage-status">{usageStatusLabel(usage)}</p>
      <UsageBanner usage={usage} />

      <div className="usage-provider">
        <h3>Postmark (primary)</h3>
        <div className="usage-grid">
          <div className="usage-stat">
            <div className="muted">Sent today</div>
            <div className="value">{postmark.sent_today ?? 0}</div>
          </div>
          <div className="usage-stat">
            <div className="muted">Sent this month (UTC)</div>
            <div className="value">{postmark.sent_this_month ?? 0}</div>
          </div>
          <div className="usage-stat">
            <div className="muted">Monthly limit</div>
            <div className="value">{postmark.monthly_limit ?? usage.monthly_limit}</div>
          </div>
          <div className="usage-stat">
            <div className="muted">Remaining</div>
            <div className="value">{postmark.remaining ?? usage.remaining}</div>
          </div>
        </div>
      </div>

      <div className="usage-provider">
        <h3>Resend (fallback)</h3>
        {!resend.configured ? (
          <p className="help-text">
            Not configured. Set <code>RESEND_API_KEY</code> so mail continues
            after Postmark’s monthly quota.
          </p>
        ) : (
          <div className="usage-grid">
            <div className="usage-stat">
              <div className="muted">Sent today (quota units)</div>
              <div className="value">{resend.sent_today ?? 0}</div>
            </div>
            <div className="usage-stat">
              <div className="muted">Daily remaining</div>
              <div className="value">{resend.remaining_today ?? 0}</div>
            </div>
            <div className="usage-stat">
              <div className="muted">Sent this month</div>
              <div className="value">{resend.sent_this_month ?? 0}</div>
            </div>
            <div className="usage-stat">
              <div className="muted">Monthly remaining</div>
              <div className="value">{resend.remaining_month ?? 0}</div>
            </div>
          </div>
        )}
      </div>

      <h3>This month by type</h3>
      <ul>
        <li>Magic links: {byKind.magic_link || 0}</li>
        <li>Contact forms: {byKind.contact_form || 0}</li>
        <li>Membership enquiries: {byKind.membership_inquiry || 0}</li>
        <li>For Sale contacts: {byKind.for_sale_contact || 0}</li>
      </ul>
      {usage.by_provider_month ? (
        <p className="help-text">
          Provider split this month: Postmark {usage.by_provider_month.postmark || 0},
          Resend {usage.by_provider_month.resend || 0}.
        </p>
      ) : null}
    </div>
  );
}

function PendingPanel({ onChanged }) {
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api
      .adminAccessRequests(true)
      .then((d) => setRequests(d.requests || []))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enroll = async (id) => {
    const first_name = window.prompt("First name for this member?");
    if (!first_name) return;
    setBusyId(id);
    try {
      await api.adminEnroll(id, first_name.trim());
      load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const block = async (id) => {
    if (!window.confirm("Block this email from requesting access?")) return;
    setBusyId(id);
    try {
      await api.adminBlock(id);
      load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {error ? <div className="message message-error">{error}</div> : null}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Status</th>
            <th>Attempts</th>
            <th>Last seen</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id}>
              <td>{r.email}</td>
              <td>{r.status}</td>
              <td>{r.attempt_count}</td>
              <td>{r.last_seen_at}</td>
              <td>
                {r.status === "pending" ? (
                  <div className="inline-actions">
                    <button
                      className="btn"
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => enroll(r.id)}
                    >
                      Enroll
                    </button>
                    <button
                      className="btn btn-danger"
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => block(r.id)}
                    >
                      Block
                    </button>
                  </div>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
            </tr>
          ))}
          {!requests.length ? (
            <tr>
              <td colSpan={5}>No pending or blocked access requests.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function MembersPanel() {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    api
      .adminMembers()
      .then((d) => setMembers(d.members || []))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    try {
      await api.adminAddMember(email, firstName);
      setEmail("");
      setFirstName("");
      setMessage("Member added.");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const patch = async (id, body) => {
    setError("");
    try {
      await api.adminPatchMember(id, body);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently remove this member?")) return;
    try {
      await api.adminDeleteMember(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      {error ? <div className="message message-error">{error}</div> : null}
      {message ? <div className="message message-success">{message}</div> : null}

      <form onSubmit={add} style={{ marginBottom: "1.5rem" }}>
        <h3>Add member</h3>
        <p className="help-text">
          Use the same email the member uses with Range Mate.
        </p>
        <div className="form-field">
          <label htmlFor="add-email">Email</label>
          <input
            id="add-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="add-first">First name</label>
          <input
            id="add-first"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <button className="btn" type="submit">
          Add
        </button>
      </form>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Admin</th>
            <th>Active</th>
            <th>Last logged in</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id}>
              <td>{m.first_name}</td>
              <td>{m.email}</td>
              <td>{m.is_admin ? "Yes" : "No"}</td>
              <td>{m.is_active ? "Yes" : "No"}</td>
              <td>{formatLastLogin(m.last_login_at)}</td>
              <td>
                <div className="inline-actions">
                  {!m.is_admin ? (
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => patch(m.id, { is_admin: true })}
                    >
                      Make admin
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => patch(m.id, { is_admin: false })}
                    >
                      Demote
                    </button>
                  )}
                  {m.is_active ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => patch(m.id, { is_active: false })}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => patch(m.id, { is_active: true })}
                    >
                      Reactivate
                    </button>
                  )}
                  <button
                    className="btn btn-danger"
                    type="button"
                    onClick={() => remove(m.id)}
                  >
                    Remove
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BulkPanel() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const members = await parseMembersSpreadsheet(file);
      const data = await api.adminBulkMembers(members);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <p className="help-text">
        Upload a CSV or Excel export with <strong>Email</strong> and{" "}
        <strong>First Name</strong> columns (Range Mate roster export). Max 500
        rows.
      </p>
      <input
        type="file"
        accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={onFile}
        disabled={busy}
      />
      {busy ? <p>Importing…</p> : null}
      {error ? <div className="message message-error">{error}</div> : null}
      {result ? (
        <div
          className={`message ${
            result.errors?.length ? "message-error" : "message-success"
          }`}
        >
          Added {result.added}, updated {result.updated || 0}, skipped{" "}
          {result.skipped}, errors {result.errors?.length || 0}.
        </div>
      ) : null}
      {result?.errors?.length ? (
        <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
          {JSON.stringify(result.errors, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [eventType, setEventType] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .adminAuditLogs({
        limit: 100,
        event_type: eventType || undefined,
        email: email || undefined,
      })
      .then((d) => setLogs(d.logs || []))
      .catch((e) => setError(e.message));
  }, [eventType, email]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="members-actions" style={{ marginBottom: "1rem" }}>
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          aria-label="Filter event type"
        >
          <option value="">All events</option>
          <option value="login_success">login_success</option>
          <option value="logout">logout</option>
          <option value="member_added">member_added</option>
          <option value="members_bulk_imported">members_bulk_imported</option>
          <option value="member_promoted_admin">member_promoted_admin</option>
          <option value="member_demoted_admin">member_demoted_admin</option>
          <option value="member_deactivated">member_deactivated</option>
          <option value="member_reactivated">member_reactivated</option>
          <option value="member_removed">member_removed</option>
          <option value="access_request_enrolled">access_request_enrolled</option>
          <option value="access_request_blocked">access_request_blocked</option>
        </select>
        <input
          type="email"
          placeholder="Filter by email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn btn-outline" type="button" onClick={load}>
          Refresh
        </button>
      </div>
      {error ? <div className="message message-error">{error}</div> : null}
      <table className="admin-table">
        <thead>
          <tr>
            <th>When (UTC)</th>
            <th>Event</th>
            <th>Actor</th>
            <th>Target</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id}>
              <td>{log.created_at}</td>
              <td>{log.event_type}</td>
              <td>{log.actor_email || "—"}</td>
              <td>{log.target_email || "—"}</td>
              <td className="muted">{log.details_json || "—"}</td>
            </tr>
          ))}
          {!logs.length ? (
            <tr>
              <td colSpan={5}>No audit entries.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function AdminPage() {
  const { member, loading, refresh } = useAuth();
  const [tab, setTab] = useState("usage");

  if (loading) {
    return (
      <>
        <Header />
        <main className="admin-layout">
          <p>Loading…</p>
        </main>
        <Footer />
      </>
    );
  }

  if (!member) {
    return <Navigate to="/members" replace />;
  }

  if (!member.is_admin) {
    return <Navigate to="/members" replace />;
  }

  return (
    <>
      <Header />
      <main className="admin-layout">
        <div className="members-toolbar">
          <div>
            <h1>Admin area</h1>
            <p className="help-text">Signed in as {member.email}</p>
          </div>
          <Link className="btn btn-outline" to="/members">
            ← Back to members
          </Link>
        </div>

        <div className="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`admin-tab${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.id === "pending" && member.pending_access_count > 0 ? (
                <span className="badge">{member.pending_access_count}</span>
              ) : null}
            </button>
          ))}
        </div>

        {tab === "usage" ? <UsagePanel /> : null}
        {tab === "pending" ? (
          <PendingPanel onChanged={() => refresh()} />
        ) : null}
        {tab === "members" ? <MembersPanel /> : null}
        {tab === "bulk" ? <BulkPanel /> : null}
        {tab === "audit" ? <AuditPanel /> : null}
      </main>
      <Footer />
    </>
  );
}

export default AdminPage;
